import { useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import { RefreshCw } from 'lucide-react';

export interface CaptchaHandle {
  validate: (value: string) => boolean;
  regenerate: () => void;
}

interface CaptchaProps {
  length?: number;
  onChange?: (code: string) => void;
}

const CHAR_POOL = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

function generateCode(length: number): string {
  let result = '';
  for (let i = 0; i < length; i += 1) {
    result += CHAR_POOL[Math.floor(Math.random() * CHAR_POOL.length)];
  }
  return result;
}

export const Captcha = forwardRef<CaptchaHandle, CaptchaProps>(({ length = 5, onChange }, ref) => {
  const [code, setCode] = useState<string>(() => generateCode(length));
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const regenerate = () => setCode(generateCode(length));

  useImperativeHandle(ref, () => ({
    validate: (value: string) => value.trim().toUpperCase() === code,
    regenerate,
  }), [code]);

  useEffect(() => {
    // notify parent when code changes (including initial mount)
    onChange?.(code);

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;

    // background
    ctx.fillStyle = `hsl(${Math.floor(Math.random() * 360)}, 35%, 95%)`;
    ctx.fillRect(0, 0, width, height);

    // draw interference lines
    for (let i = 0; i < 6; i += 1) {
      ctx.strokeStyle = `hsla(${Math.floor(Math.random() * 360)}, 70%, 45%, 0.35)`;
      ctx.beginPath();
      ctx.moveTo(Math.random() * width, Math.random() * height);
      ctx.bezierCurveTo(
        Math.random() * width, Math.random() * height,
        Math.random() * width, Math.random() * height,
        Math.random() * width, Math.random() * height
      );
      ctx.stroke();
    }

    // draw code characters
    const charWidth = width / (code.length + 1);
    for (let i = 0; i < code.length; i += 1) {
      const ch = code[i];
      const fontSize = 20 + Math.random() * 8;
      const angle = (Math.random() - 0.5) * 0.6; // -~17°..~17°
      ctx.save();
      ctx.translate(charWidth * (i + 1), height / 2);
      ctx.rotate(angle);
      ctx.font = `${fontSize}px ui-sans-serif, system-ui`;
      ctx.fillStyle = `hsl(${Math.floor(Math.random() * 360)}, 70%, 35%)`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(ch, 0, 0);
      ctx.restore();
    }

    // dots
    for (let i = 0; i < 50; i += 1) {
      ctx.fillStyle = `hsla(${Math.floor(Math.random() * 360)}, 70%, 35%, 0.25)`;
      ctx.beginPath();
      ctx.arc(Math.random() * width, Math.random() * height, Math.random() * 1.5 + 0.2, 0, Math.PI * 2);
      ctx.fill();
    }
  }, [code]);

  return (
    <div className="flex items-center gap-2">
      <canvas
        ref={canvasRef}
        width={120}
        height={40}
        className="rounded border border-gray-300 bg-white"
      />
      <button
        type="button"
        onClick={regenerate}
        className="inline-flex items-center px-2 py-2 rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
        aria-label="Refresh CAPTCHA"
        title="Refresh CAPTCHA"
      >
        <RefreshCw size={16} />
      </button>
    </div>
  );
});

Captcha.displayName = 'Captcha';

export default Captcha;


